import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { pushNotificationService } from '../services/pushNotificationService'
import { initializeFirebaseMessagingForeground } from '../services/firebaseMessagingForeground'
import { getFcmVapidKey, firebaseConfigSource } from '../config/firebase-config'
import { clearPushSessionStorage, rememberPushTokenForUser, syncPushForAuthenticatedUser } from '../services/pushSessionService'
import { getServiceWorkerConfig } from '../utils/serviceWorkerHelpers'
import { getFcmTokenWithSubscriptionRetry } from '../utils/pushSubscriptionHelpers'
import { extractPushTokensArray, isPushTokenActive } from '../utils/pushTokensResponse'
import { Bell, BellOff, Loader2, AlertCircle } from 'lucide-react'
import Button from './ui/Button'

/**
 * Activa/desactiva notificaciones push (FCM).
 * Uso principal en admin: pestaña **Sistema** → ruta `/configuracion/sistema` (Configuracion.jsx).
 * También se reutiliza en Notificaciones y móvil.
 */
export default function PushNotificationButton() {
  const { user, isAuthenticated } = useAuth()
  const [token, setToken] = useState(null)
  const [isSupported, setIsSupported] = useState(false)
  const [firebaseSupported, setFirebaseSupported] = useState(false)
  const [swScopeInfo, setSwScopeInfo] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checkingSupport, setCheckingSupport] = useState(true)
  const [backendActiveTokenCount, setBackendActiveTokenCount] = useState(0)
  const [backendHasCurrentToken, setBackendHasCurrentToken] = useState(false)
  const [backendHasCurrentDevice, setBackendHasCurrentDevice] = useState(false)
  const [lastRemotePush, setLastRemotePush] = useState(null)
  const [lastTokenSyncStatus, setLastTokenSyncStatus] = useState('')
  const autoRepairAttemptedRef = useRef(false)

  // Verificar soporte del navegador e inicializar Firebase Messaging
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 
        'serviceWorker' in navigator &&
        'Notification' in window &&
        'PushManager' in window
      
      setIsSupported(supported)
      
      if (supported) {
        try {
          const { isSupported: isFirebaseMessagingSupported } = await import('firebase/messaging')
          const firebaseOk = await isFirebaseMessagingSupported()
          setFirebaseSupported(firebaseOk)
          if (!firebaseOk) {
            setError('Firebase Messaging no está soportado en este navegador/dispositivo para notificaciones push web.')
          }
        } catch {
          setFirebaseSupported(false)
          setError('No se pudo verificar soporte de Firebase Messaging en este navegador.')
        }

        try {
          const registration = await navigator.serviceWorker.ready
          setSwScopeInfo(registration?.scope || '')
        } catch {
          setSwScopeInfo('')
        }

        // Inicializar Firebase Messaging para manejar mensajes en foreground
        await initializeFirebaseMessagingForeground()
      } else {
        setError('Tu navegador no soporta notificaciones push. Usa Chrome, Firefox o Edge.')
      }
      
      setCheckingSupport(false)
    }
    
    checkSupport()
  }, [])

  // Verificar si ya hay un token registrado
  useEffect(() => {
    if (isAuthenticated && user) {
      checkExistingTokens()
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isAuthenticated) return
    refreshBackendTokenDiagnostics(token).catch(() => {})
  }, [isAuthenticated, token])

  useEffect(() => {
    const handleRemotePushEvent = (event) => {
      const detail = event?.detail || {}
      setLastRemotePush({
        source: detail.source || 'unknown',
        title: detail.title || 'Nueva notificación',
        at: detail.at || Date.now(),
        notificacionId: detail.notificacionId || null,
      })
    }

    window.addEventListener('push-remote-received', handleRemotePushEvent)
    return () => {
      window.removeEventListener('push-remote-received', handleRemotePushEvent)
    }
  }, [])

  const checkExistingTokens = async () => {
    try {
      const { getMessaging, getToken } = await import('firebase/messaging')
      const firebaseConfig = await import('../config/firebase-config')
      const { initializeApp, getApps } = await import('firebase/app')

      const apps = getApps()
      const app = apps.length > 0 ? apps[0] : initializeApp(firebaseConfig.default)
      const messaging = getMessaging(app)
      const vapidKey = getFcmVapidKey()

      if (vapidKey && Notification.permission === 'granted') {
        const registration = await navigator.serviceWorker.ready
        let currentToken = null

        try {
          currentToken = await getFcmTokenWithSubscriptionRetry(getToken, messaging, {
            vapidKey,
            serviceWorkerRegistration: registration
          })
        } catch (e1) {
          console.warn('[Push] Sesión: primer intento FCM:', e1?.message || e1)
        }

        if (!currentToken) {
          try {
            currentToken = await getFcmTokenWithSubscriptionRetry(getToken, messaging, {
              vapidKey,
              serviceWorkerRegistration: registration,
              clearSubscriptionFirst: true
            })
          } catch (e2) {
            console.warn('[Push] Sesión: segundo intento FCM (limpiando suscripción):', e2?.message || e2)
          }
        }

        if (currentToken) {
          console.log('✅ Token FCM obtenido del navegador:', currentToken.substring(0, 20) + '...')
          setToken(currentToken)
          await registerTokenInBackend(currentToken)
          return
        }
      }

      const response = await pushNotificationService.getTokens()
      const list = extractPushTokensArray(response)
      const activeTokens = list.filter(isPushTokenActive)
      if (activeTokens.length > 0) {
        console.info(
          '[Push] Hay token(s) registrados en el servidor para tu cuenta, pero este navegador no devolvió token FCM ' +
            '(otro dispositivo, permiso no concedido aquí o suscripción caducada). Pulsa «Activar notificaciones Push» para registrar este equipo.'
        )
        setToken(null)
      }
    } catch (err) {
      console.warn('No se pudieron verificar tokens existentes:', err)
      setToken(null)
    }
    await refreshBackendTokenDiagnostics(null).catch(() => {})
  }

  async function refreshBackendTokenDiagnostics(currentToken = token) {
    try {
      const response = await pushNotificationService.getTokens()
      const list = extractPushTokensArray(response)
      if (response?.success === false && list.length === 0) {
        setBackendActiveTokenCount(0)
        setBackendHasCurrentToken(false)
        setBackendHasCurrentDevice(false)
        return { hasCurrentToken: false, hasCurrentDevice: false }
      }

      const activeTokens = list.filter(isPushTokenActive)
      setBackendActiveTokenCount(activeTokens.length)
      const currentDeviceId = localStorage.getItem('device_id')
      const hasCurrentDevice = currentDeviceId
        ? activeTokens.some((item) => {
            const candidate = item?.device_id || item?.deviceId || item?.id_dispositivo || item?.idDispositivo || ''
            return String(candidate).trim() === String(currentDeviceId).trim()
          })
        : false
      setBackendHasCurrentDevice(hasCurrentDevice)

      const normalizedCurrentToken = String(currentToken || '').trim()
      if (!normalizedCurrentToken) {
        setBackendHasCurrentToken(false)
        return { hasCurrentToken: false, hasCurrentDevice: hasCurrentDevice }
      }

      const hasCurrentToken = activeTokens.some((item) => {
        const candidates = [
          item?.token,
          item?.token_actual,
          item?.tokenActual,
          item?.token_preview,
          item?.tokenPreview,
          item?.token_masked,
          item?.tokenMasked,
        ]
          .map((value) => String(value || '').trim())
          .filter(Boolean)

        if (candidates.length === 0) return false
        return candidates.some((candidate) => {
          if (candidate === normalizedCurrentToken) return true
          if (candidate.includes('...')) {
            const [start = '', end = ''] = candidate.split('...')
            return normalizedCurrentToken.startsWith(start) && normalizedCurrentToken.endsWith(end)
          }
          return false
        })
      })
      setBackendHasCurrentToken(hasCurrentToken)
      return { hasCurrentToken, hasCurrentDevice }
    } catch (diagError) {
      console.warn('No se pudo cargar diagnóstico de tokens backend:', diagError?.message || diagError)
      setBackendActiveTokenCount(0)
      setBackendHasCurrentToken(false)
      setBackendHasCurrentDevice(false)
      return { hasCurrentToken: false, hasCurrentDevice: false }
    }
  }

  const getCurrentBrowserToken = async () => {
    const { getMessaging, getToken } = await import('firebase/messaging')
    const firebaseConfig = await import('../config/firebase-config')
    const { initializeApp, getApps } = await import('firebase/app')

    const apps = getApps()
    const app = apps.length > 0 ? apps[0] : initializeApp(firebaseConfig.default)
    const messaging = getMessaging(app)
    const vapidKey = getFcmVapidKey()
    if (!vapidKey) {
      throw new Error('VAPID key no configurada para obtener token')
    }

    const sw = getServiceWorkerConfig()
    await navigator.serviceWorker.register(sw.path, { scope: sw.scope })
    const registration = await navigator.serviceWorker.ready

    const freshToken = await getFcmTokenWithSubscriptionRetry(getToken, messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
      clearSubscriptionFirst: true
    })

    if (!freshToken) {
      throw new Error('No se pudo obtener token FCM del navegador')
    }

    return freshToken
  }

  const handleRemoteTokenResync = async () => {
    try {
      setIsLoading(true)
      setError(null)
      setLastTokenSyncStatus('')

      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        setError('Debes conceder permisos de notificación antes de sincronizar el token remoto.')
        return
      }

      // Forzar nueva identidad de dispositivo para evitar registros atascados por device_id previo
      localStorage.removeItem('device_id')
      const synced = await syncPushForAuthenticatedUser(user)
      const freshToken = await getCurrentBrowserToken()
      setToken(freshToken)

      if (!synced) {
        // Fallback explícito en caso de que falle la sincronización automática.
        await registerTokenInBackend(freshToken)
      }

      const diag = await refreshBackendTokenDiagnostics(freshToken)

      // Confirmación real: considerar ok si token o dispositivo aparecen en backend.
      const tokenVisible = Boolean(diag?.hasCurrentToken)
      const deviceVisible = Boolean(diag?.hasCurrentDevice)
      if (tokenVisible || deviceVisible) {
        setLastTokenSyncStatus('Sincronización remota completada')
      } else {
        setLastTokenSyncStatus('Sincronización enviada, pero backend aún no refleja token/dispositivo')
      }
    } catch (syncError) {
      console.error('❌ Error en re-sincronización de token remoto:', syncError)
      setError(syncError?.message || 'No se pudo sincronizar token remoto')
      setLastTokenSyncStatus('Falló la sincronización remota')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestPermission = async () => {
    if (!isSupported) {
      setError('Las notificaciones push no están soportadas en este navegador')
      return
    }
    if (!firebaseSupported) {
      setError(
        'Firebase Messaging no está disponible en este navegador. No se puede obtener token FCM ni registrar en el servidor.'
      )
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      // Solicitar permiso de notificaciones
      const permission = await Notification.requestPermission()
      
      if (permission !== 'granted') {
        setError('Permiso de notificaciones denegado. Por favor, habilita las notificaciones en la configuración de tu navegador.')
        setIsLoading(false)
        return
      }

      // Registrar/actualizar el Service Worker principal (unico para app + push)
      let registration
      try {
        const sw = getServiceWorkerConfig()
        registration = await navigator.serviceWorker.register(sw.path, {
          scope: sw.scope
        })
        console.log('✅ Service Worker registrado:', registration.scope)

        // Esperar a que el Service Worker esté activo (con timeout)
        if (registration.installing) {
          console.log('⏳ Service Worker instalando...')
          await Promise.race([
            new Promise((resolve) => {
              registration.installing.addEventListener('statechange', function() {
                if (this.state === 'activated' || this.state === 'redundant') {
                  console.log(`✅ Service Worker ${this.state}`)
                  resolve()
                }
              })
              // Si ya está activado, resolver inmediatamente
              if (registration.installing.state === 'activated') {
                resolve()
              }
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout esperando Service Worker')), 10000))
          ]).catch((err) => {
            console.warn('⚠️ Timeout esperando Service Worker, continuando...', err)
          })
        } else if (registration.waiting) {
          console.log('⏳ Service Worker esperando...')
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          await Promise.race([
            new Promise((resolve) => {
              registration.waiting.addEventListener('statechange', function() {
                if (this.state === 'activated' || this.state === 'redundant') {
                  console.log(`✅ Service Worker ${this.state}`)
                  resolve()
                }
              })
              if (registration.waiting.state === 'activated') {
                resolve()
              }
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout esperando Service Worker')), 10000))
          ]).catch((err) => {
            console.warn('⚠️ Timeout esperando Service Worker, continuando...', err)
          })
        } else if (registration.active) {
          console.log('✅ Service Worker ya está activo')
        }

        // Esperar a que el Service Worker esté listo (con timeout)
        try {
          await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout esperando Service Worker ready')), 5000))
          ])
          console.log('✅ Service Worker listo')
        } catch (err) {
          console.warn('⚠️ Timeout esperando Service Worker ready, continuando...', err)
        }
      } catch (swError) {
        console.error('❌ Error al registrar Service Worker:', swError)
        setError(`Error al registrar Service Worker: ${swError.message}. Verifica que sw.js esté en la carpeta public/`)
        setIsLoading(false)
        return
      }

      // Intentar obtener token FCM usando Firebase
      try {
        const { initializeApp } = await import('firebase/app')
        const { getMessaging, getToken } = await import('firebase/messaging')
        const firebaseConfig = await import('../config/firebase-config')
        
        let app
        try {
          app = initializeApp(firebaseConfig.default)
        } catch (initError) {
          // Si ya está inicializado, obtener la app existente
          const { getApps } = await import('firebase/app')
          const apps = getApps()
          if (apps.length > 0) {
            app = apps[0]
          } else {
            throw initError
          }
        }

        const messaging = getMessaging(app)
        const vapidKey = getFcmVapidKey()
        
        if (!vapidKey) {
          throw new Error('VAPID key no configurada. Verifica la configuración de Firebase')
        }

        console.log('🔑 VAPID Key configurada:', vapidKey.substring(0, 20) + '...')
        console.log('🔥 Firebase config source:', firebaseConfigSource)
        console.log('📱 Verificando Service Worker...')

        // Asegurar que el Service Worker esté completamente listo y activo
        let readyRegistration
        try {
          readyRegistration = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
          ])
          
          // Verificar que el Service Worker esté realmente activo
          if (readyRegistration.active) {
            console.log('✅ Service Worker activo:', readyRegistration.active.state)
          } else {
            console.warn('⚠️ Service Worker no está activo, esperando...')
            // Esperar un poco más
            await new Promise(resolve => setTimeout(resolve, 1000))
            readyRegistration = await navigator.serviceWorker.ready
          }
        } catch (err) {
          console.warn('⚠️ No se pudo obtener Service Worker ready, usando registration actual:', err)
          readyRegistration = registration
        }
        
        // Verificar que tengamos un Service Worker válido
        if (!readyRegistration || !readyRegistration.active) {
          throw new Error('Service Worker no está activo. Por favor, recarga la página e intenta de nuevo.')
        }
        
        console.log('📱 Intentando obtener token FCM con Service Worker activo...')
        console.log('📋 Detalles del Service Worker:', {
          state: readyRegistration.active.state,
          scope: readyRegistration.scope,
          scriptURL: readyRegistration.active.scriptURL
        })
        
        // Verificar que estamos en localhost o HTTPS
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        const isHTTPS = window.location.protocol === 'https:'
        console.log('🌐 Entorno:', {
          hostname: window.location.hostname,
          protocol: window.location.protocol,
          isLocalhost,
          isHTTPS,
          url: window.location.href
        })
        
        if (!isLocalhost && !isHTTPS) {
          console.warn('⚠️ Firebase FCM requiere HTTPS en producción. En desarrollo, localhost funciona correctamente.')
        }
        
        // Validar formato de VAPID key (debe ser una string base64 válida)
        if (!vapidKey || vapidKey.length < 80) {
          throw new Error('VAPID key inválida. Debe tener al menos 80 caracteres. Verifica tu archivo .env')
        }
        
        console.log('🔍 VAPID Key length:', vapidKey.length)
        console.log('🔍 VAPID Key format:', /^[A-Za-z0-9_-]+$/.test(vapidKey) ? 'Válido' : 'Formato inválido')
        console.log('🔍 VAPID Key preview:', vapidKey.substring(0, 30) + '...' + vapidKey.substring(vapidKey.length - 10))
        
        // Intentar obtener el token FCM
        // Nota: No pasar serviceWorkerRegistration si ya está en ready
        let fcmToken
        try {
          console.log('🔑 Intentando obtener token FCM...')
          console.log('📋 Service Worker registration:', {
            active: !!readyRegistration.active,
            installing: !!readyRegistration.installing,
            waiting: !!readyRegistration.waiting,
            scope: readyRegistration.scope
          })
          
          // Esperar un momento adicional para asegurar que el Service Worker esté completamente listo
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Verificar que el push manager esté disponible
          if (!readyRegistration.pushManager) {
            throw new Error('PushManager no está disponible en el Service Worker')
          }
          
          console.log('✅ PushManager disponible')
          // No usar PushManager.permissionState(): en Chrome dispara
          // "Push subscriptions that don't enable userVisibleOnly are not supported"
          // y puede romper el registro FCM. Notification.permission ya se validó arriba.

          // Intentar obtener el token de Firebase directamente
          console.log('🔑 Obteniendo token FCM de Firebase...')
          console.log('📋 VAPID Key (primeros 30 chars):', vapidKey.substring(0, 30))
          console.log('📋 VAPID Key length:', vapidKey.length)
          
          fcmToken = await Promise.race([
            getFcmTokenWithSubscriptionRetry(getToken, messaging, {
              vapidKey: vapidKey,
              serviceWorkerRegistration: readyRegistration,
              clearSubscriptionFirst: true
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout obteniendo token FCM (15s)')), 15000))
          ])
          console.log('✅ Token FCM obtenido exitosamente')
        } catch (tokenError) {
          const tokenErrorMessage = tokenError?.message || ''
          const normalizedTokenError = tokenErrorMessage.toLowerCase()

          console.error('❌ Error detallado al obtener token:', {
            message: tokenErrorMessage,
            name: tokenError.name,
            code: tokenError.code,
            stack: tokenError.stack?.substring(0, 500)
          })

          const hasCredentialError =
            normalizedTokenError.includes('missing required authentication credential') ||
            normalizedTokenError.includes('request is missing required authentication credential') ||
            normalizedTokenError.includes('messaging/token-subscribe-failed')

          if (hasCredentialError) {
            let errorMessage = '❌ Firebase rechazó la suscripción por credenciales faltantes o inválidas.\n\n'
            errorMessage += 'Esto normalmente indica un problema con API Key/restricciones del proyecto Firebase.\n\n'
            errorMessage += '🔍 VALIDAR EN FIREBASE / GOOGLE CLOUD:\n\n'
            errorMessage += '1. API KEY WEB:\n'
            errorMessage += '   - Firebase Console → Project Settings → General → Web API Key\n'
            errorMessage += '   - Debe corresponder al proyecto Artemis usado por la app\n'
            errorMessage += '   - Si usas una key custom, evita placeholders o valores de otro proyecto\n\n'
            errorMessage += '2. RESTRICCIONES DE API KEY (Google Cloud):\n'
            errorMessage += '   - APIs permitidas: Firebase Cloud Messaging API + Firebase Installations API\n'
            errorMessage += '   - Restricciones HTTP referrer: incluir tu dominio y localhost (si aplica)\n\n'
            errorMessage += '3. CLOUD MESSAGING:\n'
            errorMessage += '   - Firebase Console → Cloud Messaging → Web Push certificate válido\n'
            errorMessage += '   - VAPID debe pertenecer al mismo proyecto Firebase\n\n'
            errorMessage += '4. LIMPIEZA LOCAL:\n'
            errorMessage += '   - DevTools → Application → Service Workers → Unregister\n'
            errorMessage += '   - DevTools → Application → Storage → Clear site data\n'
            errorMessage += '   - Recargar y volver a activar notificaciones\n\n'
            errorMessage += `ℹ️ Fuente de config detectada en la app: ${firebaseConfigSource}`

            throw new Error(errorMessage)
          }
          
          // Si el error es de registro (código 20), es un problema con la VAPID key o la configuración de Firebase
          if (tokenError.code === 20 || 
              tokenErrorMessage.includes('Registration failed') || 
              tokenErrorMessage.includes('push service error') ||
              tokenErrorMessage.includes('messaging/registration-token-not-ready')) {
            
            let errorMessage = '❌ Error de registro con Firebase Push Service (Código 20)\n\n'
            errorMessage += 'Este error generalmente indica un problema con la VAPID key o la configuración de Firebase.\n\n'
            errorMessage += '🔍 PASOS PARA SOLUCIONAR:\n\n'
            errorMessage += '1. VERIFICAR VAPID KEY EN FIREBASE:\n'
            errorMessage += '   - Ve a Firebase Console: https://console.firebase.google.com/\n'
            errorMessage += '   - Selecciona tu proyecto (artemis-fcfad)\n'
            errorMessage += '   - Ve a Configuración (⚙️) → Cloud Messaging\n'
            errorMessage += '   - En "Web Push certificates", verifica o genera una nueva VAPID key\n'
            errorMessage += '   - Copia la clave COMPLETA (debe tener ~87 caracteres)\n\n'
            errorMessage += '2. VERIFICAR VAPID KEY EN CONFIG:\n'
            errorMessage += '   - Verifica la variable VITE_FCM_VAPID_KEY en el entorno de despliegue\n'
            errorMessage += '   - Si no está, la app usa un fallback por defecto del proyecto Artemis\n'
            errorMessage += '   - NO debe tener comillas, NO debe tener espacios\n'
            errorMessage += '   - Debe ser EXACTAMENTE la misma que en Firebase\n\n'
            errorMessage += '3. HABILITAR API FCM V1:\n'
            errorMessage += '   - Ve a Google Cloud Console: https://console.cloud.google.com/\n'
            errorMessage += '   - Selecciona tu proyecto\n'
            errorMessage += '   - Ve a APIs & Services → Library\n'
            errorMessage += '   - Busca "Firebase Cloud Messaging API"\n'
            errorMessage += '   - Asegúrate de que esté HABILITADA (no la versión "heredada")\n\n'
            errorMessage += '4. LIMPIAR Y REINICIAR:\n'
            errorMessage += '   - Abre DevTools (F12) → Application → Service Workers\n'
            errorMessage += '   - Haz clic en "Unregister" para todos los Service Workers\n'
            errorMessage += '   - Cierra y reinicia el servidor de desarrollo (Ctrl+C y npm run dev)\n'
            errorMessage += '   - Recarga la página completamente (Ctrl+Shift+R)\n\n'
            errorMessage += '5. VERIFICAR CONFIGURACIÓN:\n'
            errorMessage += '   - Verifica que estés usando HTTPS o localhost (no IP)\n'
            errorMessage += '   - Prueba en otro navegador (Chrome, Firefox, Edge)\n'
            errorMessage += '   - Verifica que no haya firewall bloqueando fcm.googleapis.com\n\n'
            errorMessage += '💡 Si el problema persiste después de estos pasos, verifica los logs del backend.'
            
            throw new Error(errorMessage)
          }
          
          // Para otros errores, mostrar el mensaje original
          throw tokenError
        }

        if (fcmToken) {
          console.log('✅ Token FCM obtenido:', fcmToken.substring(0, 20) + '...')
          setToken(fcmToken)
          // Registrar token en el backend
          try {
            await registerTokenInBackend(fcmToken)
            console.log('✅ Proceso completado exitosamente')
          } catch (registerError) {
            console.error('❌ Error al registrar token en backend:', registerError)
            setError(`Token obtenido pero error al registrar: ${registerError.message}`)
            // No limpiar el token si falla el registro, para que el usuario pueda intentar de nuevo
          }
        } else {
          setError('No se pudo obtener el token FCM. Verifica que el VAPID key esté configurado correctamente.')
        }
      } catch (firebaseError) {
        console.error('❌ Error con Firebase:', firebaseError)
        console.info(
          'ℹ️ Sin token FCM no se inicia el registro remoto. Corrige el error de Firebase primero.'
        )
        const errorMessage = firebaseError.message || 'Error desconocido'
        setError(
          `Error al obtener token FCM: ${errorMessage}. ` +
            'Hasta que el navegador obtenga el token, no se envía el registro al servidor.'
        )
      } finally {
        setIsLoading(false)
      }
    } catch (err) {
      console.error('Error al solicitar permiso:', err)
      setError(err.message || 'Error al solicitar permiso de notificaciones')
      setIsLoading(false)
    }
  }

  /**
   * Registrar token FCM en el backend
   * Estructura alineada con PushNotificationController::register()
   */
  const registerTokenInBackend = async (fcmToken) => {
    try {
      // Validar que el token no sea la VAPID key (error común)
      const vapidKey = getFcmVapidKey()
      if (fcmToken.length < 50 || (vapidKey && fcmToken === vapidKey)) {
        throw new Error('Token FCM inválido. Por favor, intenta activar las notificaciones nuevamente.')
      }

      // Obtener información del dispositivo
      const deviceInfo = getDeviceInfo()
      
      // Construir payload exactamente como lo espera el backend
      // Según PushNotificationController::register() línea 32-39
      const payload = {
        token: fcmToken, // required, string, max:500
        device_id: deviceInfo.device_id, // nullable, string, max:255
        platform: deviceInfo.platform, // required, in:android,ios,web
        app_version: deviceInfo.app_version, // nullable, string, max:50
        device_model: deviceInfo.device_model, // nullable, string, max:100
        os_version: deviceInfo.os_version // nullable, string, max:50
      }

      console.log('📤 Registrando token en el backend:', {
        token: fcmToken.substring(0, 20) + '...',
        platform: payload.platform,
        device_id: payload.device_id,
        device_model: payload.device_model,
        os_version: payload.os_version,
        app_version: payload.app_version
      })

      const response = await pushNotificationService.register(payload)
      
      if (response?.success) {
        console.log('✅ Token registrado exitosamente en el backend:', response.data)
        rememberPushTokenForUser(fcmToken, user)
        // Verificar que el token se guardó correctamente
        const tokensResponse = await pushNotificationService.getTokens()
        const listed = extractPushTokensArray(tokensResponse)
        if (listed.length > 0) {
          const activeTokens = listed.filter(isPushTokenActive)
          console.log(`✅ Tokens activos en el backend: ${activeTokens.length}`)
        }
        await refreshBackendTokenDiagnostics(fcmToken)
      } else {
        const errorMsg = response.error || 'Error al registrar token en el servidor'
        console.error('❌ Error al registrar token:', errorMsg)
        setError(errorMsg)
        throw new Error(errorMsg)
      }
    } catch (err) {
      console.error('❌ Error al registrar token:', err)
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.details || 
                      err.message || 
                      'Error al registrar token'
      setError(errorMsg)
      throw err // Re-lanzar para que el llamador sepa que falló
    }
  }

  useEffect(() => {
    autoRepairAttemptedRef.current = false
  }, [token])

  useEffect(() => {
    if (!isAuthenticated || !token || backendHasCurrentToken || autoRepairAttemptedRef.current) return
    autoRepairAttemptedRef.current = true
    setLastTokenSyncStatus('Intentando auto-reparar token remoto...')
    registerTokenInBackend(token)
      .then(() => setLastTokenSyncStatus('Auto-reparación de token remoto completada'))
      .catch((syncError) => {
      console.warn('No se pudo auto-reparar registro de token remoto:', syncError?.message || syncError)
      setLastTokenSyncStatus('Auto-reparación fallida')
    })
  }, [isAuthenticated, token, backendHasCurrentToken])

  const handleUnregister = async () => {
    if (!token) return

    try {
      setIsLoading(true)
      setError(null)

      const response = await pushNotificationService.unregister(token)
      
      if (response.success) {
        setToken(null)
        clearPushSessionStorage()
        console.log('✅ Token desregistrado exitosamente')
        await refreshBackendTokenDiagnostics(null)
      } else {
        setError(response.error || 'Error al desregistrar token')
      }
    } catch (err) {
      console.error('Error al desregistrar token:', err)
      setError(err.message || 'Error al desregistrar token')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Obtener ID único del dispositivo (persistente en localStorage)
   */
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id')
    if (!deviceId) {
      deviceId = 'web_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
      localStorage.setItem('device_id', deviceId)
    }
    return deviceId
  }

  /**
   * Obtener información del dispositivo/navegador
   * Alineado con la estructura que espera el backend
   */
  const getDeviceInfo = () => {
    // Obtener información del navegador
    const userAgent = navigator.userAgent
    const platform = navigator.platform
    
    // Detectar sistema operativo
    let osVersion = platform
    if (userAgent.includes('Windows')) {
      const match = userAgent.match(/Windows NT (\d+\.\d+)/)
      osVersion = match ? `Windows ${match[1]}` : 'Windows'
    } else if (userAgent.includes('Mac OS X')) {
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/)
      osVersion = match ? `macOS ${match[1].replace('_', '.')}` : 'macOS'
    } else if (userAgent.includes('Linux')) {
      osVersion = 'Linux'
    } else if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android (\d+\.\d+)/)
      osVersion = match ? `Android ${match[1]}` : 'Android'
    } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      const match = userAgent.match(/OS (\d+[._]\d+)/)
      osVersion = match ? `iOS ${match[1].replace('_', '.')}` : 'iOS'
    }
    
    // Detectar navegador y versión
    let deviceModel = userAgent
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/(\d+)/)
      deviceModel = match ? `Chrome ${match[1]}` : 'Chrome'
    } else if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/)
      deviceModel = match ? `Firefox ${match[1]}` : 'Firefox'
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/(\d+)/)
      deviceModel = match ? `Safari ${match[1]}` : 'Safari'
    } else if (userAgent.includes('Edg')) {
      const match = userAgent.match(/Edg\/(\d+)/)
      deviceModel = match ? `Edge ${match[1]}` : 'Edge'
    }
    
    // Obtener versión de la app desde package.json o env
    const appVersion = import.meta.env.VITE_APP_VERSION || 
                      import.meta.env.npm_package_version || 
                      '1.0.0'
    
    return {
      device_id: getDeviceId(),
      platform: 'web', // Siempre 'web' para el frontend
      app_version: appVersion.substring(0, 50), // Max 50 caracteres según backend
      device_model: deviceModel.substring(0, 100), // Max 100 caracteres según backend
      os_version: osVersion.substring(0, 50) // Max 50 caracteres según backend
    }
  }

  /**
   * Enviar notificación push de prueba
   * Estructura alineada con PushNotificationController::test()
   */
  const handleTestNotification = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Verificar que tengamos un token válido
      if (!token || token.length < 50) {
        setError('No hay un token FCM válido. Por favor, desactiva y reactiva las notificaciones.')
        setIsLoading(false)
        return
      }

      console.log('🧪 Enviando notificación de prueba con token:', token.substring(0, 20) + '...')

      // Construir payload exactamente como lo espera el backend
      // Según PushNotificationController::test() línea 199-203
      const payload = {
        titulo: 'Notificación de Prueba', // required, string, max:255
        mensaje: '¡Esta es una notificación de prueba! Si ves esto, las notificaciones push están funcionando correctamente.', // required, string
        prioridad: 'ALTA' // nullable, in:BAJA,MEDIA,ALTA,URGENTE
      }

      const response = await pushNotificationService.test(payload)

      console.log('📤 Respuesta del backend:', response)

      if (response.success) {
        // Mostrar mensaje de éxito
        alert('✅ Notificación de prueba enviada. Deberías recibirla en unos segundos.\n\nSi no la recibes:\n1. Verifica que el Service Worker esté activo\n2. Revisa la consola del navegador\n3. Verifica los logs del backend')
        console.log('✅ Notificación de prueba enviada exitosamente')
        
        // Verificar Service Worker
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready
          console.log('✅ Service Worker activo:', registration.active?.state)
        }
      } else {
        const errorMsg = response.error || 
                        response.details || 
                        'Error al enviar notificación de prueba'
        setError(errorMsg)
      }
    } catch (err) {
      console.error('❌ Error al enviar notificación de prueba:', err)
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.details || 
                      err.message || 
                      'Error al enviar notificación de prueba'
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLocalNotificationTest = async () => {
    try {
      setError(null)
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        setError('Permiso de notificaciones no concedido en el dispositivo.')
        return
      }
      if (!('serviceWorker' in navigator)) {
        setError('Service Worker no disponible.')
        return
      }

      const registration = await navigator.serviceWorker.ready
      await registration.showNotification('Prueba local Artemis', {
        body: 'Esta notificación se genera en el frontend (sin backend).',
        icon: '/icon-192.svg',
        badge: '/icon-192.svg',
        tag: `local-test-${Date.now()}`,
        requireInteraction: false,
        data: { url: '/notificaciones' }
      })
    } catch (err) {
      setError(err?.message || 'No se pudo mostrar notificación local')
    }
  }

  if (checkingSupport) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Verificando soporte...</span>
      </div>
    )
  }

  if (!isSupported) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              Notificaciones push no disponibles
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
              Tu navegador no soporta notificaciones push. Por favor, usa Chrome, Firefox o Edge para habilitar esta función.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={token ? handleUnregister : handleRequestPermission}
          disabled={
            isLoading || !isAuthenticated || (!firebaseSupported && !token)
          }
          variant={token ? "secondary" : "primary"}
          className="flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Procesando...</span>
            </>
          ) : token ? (
            <>
              <Bell className="w-5 h-5" />
              <span>Desactivar Notificaciones</span>
            </>
          ) : (
            <>
              <BellOff className="w-5 h-5" />
              <span>Activar Notificaciones Push</span>
            </>
          )}
        </Button>

        {token && (
          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <Bell className="w-5 h-5" />
            <span className="text-sm font-medium">Notificaciones activadas</span>
          </div>
        )}
      </div>


      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </p>
        </div>
      )}

      {!isAuthenticated && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Debes iniciar sesión para activar las notificaciones push.
          </p>
        </div>
      )}

      {isAuthenticated && isSupported && !firebaseSupported && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            <strong>Firebase Messaging no disponible aquí.</strong> El botón permanece deshabilitado: no se puede
            obtener token ni registrar en el servidor. Prueba con Chrome/Edge actualizado o otro navegador compatible.
          </p>
        </div>
      )}

      {isAuthenticated && firebaseSupported && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tras conceder permiso, primero el navegador obtiene un token FCM; solo entonces la app lo sincroniza con el
          servidor.
        </p>
      )}

      {isAuthenticated && !token && backendActiveTokenCount > 0 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            <strong>Hay {backendActiveTokenCount} dispositivo(s) con push registrados en tu cuenta,</strong> pero{' '}
            <strong>este navegador no tiene token activo</strong> (p. ej. otro móvil/PC o suscripción caducada).
            Pulsa <strong>Activar notificaciones Push</strong> para registrar este equipo.
          </p>
        </div>
      )}

    </div>
  )
}
